import { useState } from "react";
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, addWeeks,
} from "date-fns";
import { fr } from "date-fns/locale";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { STATUS_META, todayIso } from "@/lib/helpers";
import * as Icons from "lucide-react";

const WEEKDAYS = ["lun", "mar", "mer", "jeu", "ven", "sam", "dim"];

export function VisitCalendar({ visits, onOpen }) {
  const [mode, setMode] = useState("month"); // month | week
  const [cursor, setCursor] = useState(new Date());
  const today = todayIso();

  const byDay = {};
  (visits || []).forEach((v) => {
    if (!v.date) return;
    (byDay[v.date] = byDay[v.date] || []).push(v);
  });
  Object.values(byDay).forEach((arr) => arr.sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || "")));

  let days = [];
  if (mode === "month") {
    const gridStart = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d);
  } else {
    const wkStart = startOfWeek(cursor, { weekStartsOn: 1 });
    for (let i = 0; i < 7; i++) days.push(addDays(wkStart, i));
  }

  const title = mode === "month"
    ? format(cursor, "MMMM yyyy", { locale: fr })
    : `${format(startOfWeek(cursor, { weekStartsOn: 1 }), "d MMM", { locale: fr })} – ${format(addDays(startOfWeek(cursor, { weekStartsOn: 1 }), 6), "d MMM yyyy", { locale: fr })}`;

  const shift = (dir) => setCursor((c) => (mode === "month" ? addMonths(c, dir) : addWeeks(c, dir)));
  const cursorMonth = cursor.getMonth();

  const VisitChip = ({ v }) => {
    const dot = STATUS_META[v.status]?.dot || "bg-slate-400";
    return (
      <button onClick={() => onOpen(v.id)} data-testid={`calendar-visit-${v.reference}`}
        className="flex w-full items-center gap-1 truncate rounded bg-slate-50 px-1.5 py-1 text-left text-[11px] text-slate-700 hover:bg-emerald-50">
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dot}`} />
        {v.scheduled_time && <span className="font-mono-data text-[10px] text-slate-500">{v.scheduled_time}</span>}
        <span className="truncate">{v.client_name || v.reference}</span>
      </button>
    );
  };

  return (
    <Card className="p-3 lg:p-4" data-testid="visit-calendar">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => shift(-1)} data-testid="calendar-prev"><Icons.ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[150px] text-center font-display text-base font-semibold capitalize text-slate-800" data-testid="calendar-title">{title}</span>
          <Button variant="ghost" size="icon" onClick={() => shift(1)} data-testid="calendar-next"><Icons.ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="calendar-today" className="ml-1">Aujourd'hui</Button>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
          <button onClick={() => setMode("week")} data-testid="calendar-mode-week"
            className={`rounded-md px-3 py-1 text-sm font-medium ${mode === "week" ? "bg-emerald-600 text-white" : "text-slate-600"}`}>Semaine</button>
          <button onClick={() => setMode("month")} data-testid="calendar-mode-month"
            className={`rounded-md px-3 py-1 text-sm font-medium ${mode === "month" ? "bg-emerald-600 text-white" : "text-slate-600"}`}>Mois</button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((w) => <div key={w} className="pb-1 text-center text-[11px] font-semibold uppercase text-slate-400">{w}</div>)}
        {days.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const list = byDay[key] || [];
          const isToday = key === today;
          const outside = mode === "month" && day.getMonth() !== cursorMonth;
          const max = mode === "week" ? 12 : 3;
          return (
            <div key={key} data-testid={`calendar-day-${key}`}
              className={`rounded-lg border p-1 ${outside ? "border-slate-100 bg-slate-50/40" : "border-slate-200 bg-white"} ${mode === "week" ? "min-h-[240px]" : "min-h-[92px]"}`}>
              <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isToday ? "bg-emerald-600 text-white" : outside ? "text-slate-300" : "text-slate-600"}`}>
                {format(day, "d")}
              </div>
              <div className="space-y-1">
                {list.slice(0, max).map((v) => <VisitChip key={v.id} v={v} />)}
                {list.length > max && <span className="block px-1 text-[10px] text-slate-400">+{list.length - max} autre(s)</span>}
              </div>
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-xs text-slate-400">Les visites sont positionnées à leur date programmée. Cliquez une visite pour l'ouvrir.</p>
    </Card>
  );
}
